import React, { useState } from 'react';
import { ALARM_CATEGORY, FilterNumToEventTypes } from '../alarm_enums';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Paper,
	Switch,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * A single type filter toggle entry.
 * @typedef {Object} TypeFilterOption
 * @property {boolean} enabled True when this category is included in results.
 * @property {string} name Display label shown in the dialog.
 * @property {number} filter Alarm category integer used by FilterNumToEventTypes.
 */

/**
 * Payload emitted when the dialog closes.
 * @typedef {Object} EventTypeDialogClosePayload
 * @property {boolean} acknowledged Include acknowledged events when true.
 * @property {boolean} cleared Include cleared events when true.
 * @property {number[]|''} type Active event types expanded via FilterNumToEventTypes.
 * A blank string means all type filters are active, so callers should omit the type query parameter.
 */

/** @type {Record<string, TypeFilterOption>} */
const defaultTypeFiltering = {
	system_critical:	{ enabled: true, name: 'System Critical', filter: ALARM_CATEGORY.SYSTEM_CRITICAL },
	equipment:			{ enabled: true, name: 'Equipment', filter: ALARM_CATEGORY.EQUIPMENT },
	temperature:		{ enabled: true, name: 'Temperature', filter: ALARM_CATEGORY.TEMP },
	temp_diff:			{ enabled: true, name: 'Temp. Differential', filter: ALARM_CATEGORY.TEMP_DIFF },
	unit_voltage:		{ enabled: true, name: 'Unit Voltage', filter: ALARM_CATEGORY.UNIT_VOLT_CRIT },
	unit_impedance:		{ enabled: true, name: 'Unit Impedance', filter: ALARM_CATEGORY.UNIT_IMPED_CRIT },
	discharge:			{ enabled: true, name: 'Discharge', filter: ALARM_CATEGORY.DISCHARGE },
	abort:				{ enabled: true, name: 'Measurement Abort', filter: ALARM_CATEGORY.ABORT },
	aux:				{ enabled: true, name: 'Aux Inputs', filter: ALARM_CATEGORY.AUX },
	polling:			{ enabled: true, name: 'Polling', filter: ALARM_CATEGORY.POLLING },
};

const defaultStatusFiltering = {
	acknowledged:	{ enabled: true, name: 'Acknowledged' },
	cleared:		{ enabled: true, name: 'Cleared' },
};

// Styles for the dialog components
const sectionPaperSx = {
	padding: 1.25,
	borderRadius: 1.5,
	borderColor: 'divider',
	backgroundColor: 'grey.50',
};

const sectionTitleSx = {
	marginBottom: 0.75,
};

const optionRowSx = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	paddingX: 1,
	paddingY: 0.25,
	border: '1px solid',
	borderColor: 'divider',
	borderRadius: 1,
	backgroundColor: 'background.paper',
	minHeight: 36,
	cursor: 'pointer',
	'&:hover': {
		backgroundColor: 'action.hover',
	},
};

// Helper to clone filtering state objects, which have one level of nested objects.
function cloneFiltering(filteringState) {
	return Object.keys(filteringState).reduce((acc, key) => {
		acc[key] = { ...filteringState[key] };
		return acc;
	}, {});
}

/**
 * Shared type/status filtering dialog used by events pages.
 *
 * The dialog owns toggle state internally and emits derived filter values on close.
 *
 * @param {Object} props Component props.
 * @param {boolean} props.dialogOpen Controls whether the dialog is visible.
 * @param {(payload: EventTypeDialogClosePayload) => void} props.handleDialogClose
 * Invoked on close with the current derived filtering payload.
 * @returns {JSX.Element}
 */
export default function EventTypeFilterDialog({ dialogOpen, handleDialogClose }) {
	// Local state for toggles. This is separate from the last applied state to allow "Cancel" to revert changes.
	const [typeFiltering, setTypeFiltering] = useState(() => cloneFiltering(defaultTypeFiltering));
	const [statusFiltering, setStatusFiltering] = useState(() => cloneFiltering(defaultStatusFiltering));

	// Last applied filter states, used to restore state on "Cancel".
	const [lastAppliedTypeFiltering, setLastAppliedTypeFiltering] = useState(() => cloneFiltering(defaultTypeFiltering));
	const [lastAppliedStatusFiltering, setLastAppliedStatusFiltering] = useState(() => cloneFiltering(defaultStatusFiltering));

	const getActiveAlarmTypes = (typeFilterState) => {
		const activeAlarmTypes = [];

		Object.keys(typeFilterState).forEach((key) => {
			if (typeFilterState[key].enabled) {
				activeAlarmTypes.push(...FilterNumToEventTypes(typeFilterState[key].filter));
			}
		});

		return [...new Set(activeAlarmTypes)];
	};

	// Builds the payload emitted on dialog close based on the current toggle states.
	function buildPayload(typeFilterState, statusFilterState) {
		const allTypeActive = Object.keys(typeFilterState).every((key) => typeFilterState[key].enabled);

		return {
			acknowledged: statusFilterState.acknowledged.enabled,
			cleared: statusFilterState.cleared.enabled,
			type: allTypeActive ? '' : getActiveAlarmTypes(typeFilterState),
		};
	}

	// When the dialog is closed with "Apply", emit the current filter states.
	const handleApply = () => {
		const nextTypeFiltering = cloneFiltering(typeFiltering);
		const nextStatusFiltering = cloneFiltering(statusFiltering);

		setLastAppliedTypeFiltering(nextTypeFiltering);
		setLastAppliedStatusFiltering(nextStatusFiltering);

		handleDialogClose(buildPayload(nextTypeFiltering, nextStatusFiltering));
	};

	// When the dialog is closed with "Cancel", revert to the last applied filter states and emit those.
	const handleCancel = () => {
		const restoredTypeFiltering = cloneFiltering(lastAppliedTypeFiltering);
		const restoredStatusFiltering = cloneFiltering(lastAppliedStatusFiltering);

		setTypeFiltering(restoredTypeFiltering);
		setStatusFiltering(restoredStatusFiltering);

		handleDialogClose(buildPayload(restoredTypeFiltering, restoredStatusFiltering));
	};

	// When the dialog is closed with "Reset", revert to the default filter states and emit those.
	const handleReset = () => {
		setTypeFiltering(cloneFiltering(defaultTypeFiltering));
		setStatusFiltering(cloneFiltering(defaultStatusFiltering));
	};

	// Helper to set the enabled state of a filter toggle and update state.
	const setFilterEnabled = (setFilteringState, key, enabled) => {
		setFilteringState((prevFiltering) => ({
			...prevFiltering,
			[key]: {
				...prevFiltering[key],
				enabled,
			},
		}));
	};

	// Helper to toggle the enabled state of a filter toggle and update state.
	const toggleFilterEnabled = (setFilteringState, key) => {
		setFilteringState((prevFiltering) => ({
			...prevFiltering,
			[key]: {
				...prevFiltering[key],
				enabled: !prevFiltering[key].enabled,
			},
		}));
	};

	// Keyboard handler to toggle filter options with Enter or Space keys.
	const handleFilterRowKeyDown = (event, setFilteringState, key) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			toggleFilterEnabled(setFilteringState, key);
		}
	};

	// Helper to render filter toggle rows for a given filter state object.
	const renderFilterRows = (filteringState, setFilteringState, labelSuffix = '') => {
		return Object.keys(filteringState).map((key) => (
			<Grid item xs={12} sm={6} key={key}>
				<Box
					onClick={() => toggleFilterEnabled(setFilteringState, key)}
					onKeyDown={(event) => handleFilterRowKeyDown(event, setFilteringState, key)}
					role="button"
					tabIndex={0}
					sx={optionRowSx}
				>
					<Typography variant="body2" fontWeight="500">
						{filteringState[key].name}{labelSuffix}
					</Typography>
					<Switch
						size="small"
						checked={filteringState[key].enabled}
						onChange={(event) => setFilterEnabled(setFilteringState, event.target.name, event.target.checked)}
						onClick={(event) => event.stopPropagation()}
						name={key}
					/>
				</Box>
			</Grid>
		));
	};

	return (
		<Dialog
			open={dialogOpen}
			onClose={handleCancel}
			fullWidth
			maxWidth="sm"
			PaperProps={{
				sx: {
					borderRadius: 1.5,
				},
			}}
		>
			<DialogTitle sx={{ paddingX: 2, paddingY: 1.5 }}>
				<Box display="flex" justifyContent="space-between" alignItems="flex-start">
					<Typography variant="h6" fontFamily="Saira,sans-serif" fontWeight="600">
						Event Filters
					</Typography>
					<IconButton edge="end" color="inherit" onClick={handleCancel} aria-label="close">
						<CloseIcon />
					</IconButton>
				</Box>
			</DialogTitle>
			<DialogContent sx={{ paddingTop: 0.5, paddingX: 2, paddingBottom: 1 }}>
				<Paper
					variant="outlined"
					sx={sectionPaperSx}
				>
					<Typography fontFamily="Saira,sans-serif" fontWeight="600" sx={sectionTitleSx}>
						Type Filtering
					</Typography>
					<Grid container spacing={0.75}>
						{renderFilterRows(typeFiltering, setTypeFiltering)}
					</Grid>
				</Paper>

				<Paper
					variant="outlined"
					sx={{ marginTop: 1, ...sectionPaperSx }}
				>
					<Typography fontFamily="Saira,sans-serif" fontWeight="600" sx={sectionTitleSx}>
						Status Filtering
					</Typography>
					<Grid container spacing={0.75}>
						{renderFilterRows(statusFiltering, setStatusFiltering, ' Events')}
					</Grid>
				</Paper>
			</DialogContent>
			<DialogActions sx={{ justifyContent: 'space-between', paddingX: 2, paddingY: 1.5 }}>
				<Button onClick={handleReset} variant="outlined" color="primary" sx={{ fontWeight: 600 }}>
					Reset
				</Button>
				<Box>
					<Button onClick={handleCancel} variant="outlined" sx={{ marginRight: 1 }}>
						Cancel
					</Button>
					<Button onClick={handleApply} variant="contained">
						Apply
					</Button>
				</Box>
			</DialogActions>
		</Dialog>
	);
}